import type { DateTime } from 'luxon'
import type {
  GETCurrentTerminalGateScheduleResponse,
  GETGateScheduleEntryResponse,
  GETGateScheduleReading,
  Shift,
} from '~/services/apiClient'
import { getTerminalLabel } from '~/constants'
import { GateStatus, TerminalName } from '~/services/apiClient'
import { fastParseISO, formatDTShort } from '~/utils'

export class TerminalGateSchedule {
  entries: GateScheduleEntry[]
  terminal: TerminalName
  screenshot_url: string
  first_observed: DateTime
  last_observed: DateTime
  last_parsed_time: DateTime | null
  constructor(raw: GETCurrentTerminalGateScheduleResponse) {
    this.terminal = raw.terminal
    this.screenshot_url = raw.screenshot_url
    this.first_observed = fastParseISO(raw.first_observed)
    this.last_observed = fastParseISO(raw.last_observed)
    this.entries = raw.entries
      .map(
        (entry) =>
          new GateScheduleEntry(
            entry,
            this.first_observed,
            this.last_observed,
            this.screenshot_url
          )
      )
      .sort(
        // sort by date
        (a, b) => {
          if (a.date < b.date) return -1
          if (a.date > b.date) return 1
          return 0
        }
      )

    this.last_parsed_time = raw.last_parsed_time
      ? fastParseISO(raw.last_parsed_time)
      : null
  }

  get terminalLabel(): string {
    return gateScheduleTerminalLabel(this.terminal)
  }

  get screenshot_download_link(): string {
    return makeDownloadLink(
      this.screenshot_url,
      this.terminal,
      this.entries[0].date
    )
  }
}

export class GateScheduleEntry {
  date: DateTime
  shift: Shift
  status: GateStatus
  notes: string[]
  start_time: DateTime | null
  end_time: DateTime | null
  key: string
  first_observed: DateTime
  last_observed: DateTime
  screenshot_url: string
  constructor(
    raw: GETGateScheduleEntryResponse,
    first_observed: DateTime,
    last_observed: DateTime,
    screenshot_url: string
  ) {
    this.first_observed = first_observed
    this.last_observed = last_observed
    this.screenshot_url = screenshot_url
    this.date = fastParseISO(raw.date)
    this.shift = raw.shift
    this.status = raw.status
    this.notes = raw.notes
    this.start_time = raw.start_time ? fastParseISO(raw.start_time) : null
    this.end_time = raw.end_time ? fastParseISO(raw.end_time) : null
    this.key = gateScheduleEntryKey(this.date, this.shift)
  }

  get isClosed(): boolean {
    return this.status === GateStatus.Closed
  }
}

export class GateScheduleReading {
  terminal: TerminalName
  first_observed: DateTime
  last_observed: DateTime
  last_parsed_time: DateTime | null
  screenshot_url: string
  first_observed_raw: string
  constructor(reading: GETGateScheduleReading) {
    this.terminal = reading.terminal
    this.first_observed = fastParseISO(reading.first_observed)
    this.last_observed = fastParseISO(reading.last_observed)
    this.last_parsed_time = reading.last_parsed_time
      ? fastParseISO(reading.last_parsed_time)
      : null
    this.first_observed_raw = reading.first_observed
    this.screenshot_url = reading.screenshot_url
  }

  get last_changed_desc(): string {
    return formatDTShort(this.first_observed)
  }

  get last_changed_human_desc(): string {
    return this.first_observed.toRelative()!
  }

  get last_observed_desc(): string {
    return formatDTShort(this.last_observed)
  }

  get last_observed_human_desc(): string {
    return this.last_observed.toRelative()!
  }

  get observed_for_desc(): string {
    return this.first_observed
      .toRelative({ base: this.last_observed })!
      .replace(' ago', '')
      .replace('in ', '')
  }

  get terminal_desc(): string {
    return getTerminalLabel(this.terminal)
  }

  get screenshot_download_link(): string {
    return makeDownloadLink(
      this.screenshot_url,
      this.terminal,
      this.first_observed
    )
  }
}

export function gateScheduleEntryKey(date: DateTime, shift: Shift) {
  return `${date.toISODate()}-${shift}`
}

/* Gate schedules give the two Shippers terminals their own rows, which the
shared `getTerminalLabel` mapping doesn't distinguish. Anything rendering a
gate-schedule terminal name (row headers, "not posted" messages) must go
through this so the two stay in agreement. */
export function gateScheduleTerminalLabel(terminal: TerminalName): string {
  switch (terminal) {
    case TerminalName.StAlam:
      return 'Shippers A'
    case TerminalName.StLb:
      return 'Shippers L'
    default:
      return getTerminalLabel(terminal)
  }
}
function makeDownloadLink(
  url: string,
  terminal: TerminalName,
  date: DateTime
): string {
  const fileName = encodeURIComponent(
    `${getTerminalLabel(terminal)} Gate Schedule for ` +
      `week(s) starting ${formatDTForFilename(date)}.png`
  )
  return `${url}?download=true&filename=${fileName}`
}

function formatDTForFilename(dt: DateTime): string {
  return dt.startOf('week').toFormat('yyyy-MM-dd')
}
